import { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Bell,
  Shield,
  Palette,
  Download,
  Trash2,
  Eye,
  EyeOff,
  Save,
  AlertTriangle,
  Briefcase,
} from 'lucide-react'

import { useNavigate } from 'react-router-dom'
import { investorApi } from '@/services/api'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/hooks/use-auth'
import { useTheme } from '@/components/theme-provider'
import { useToast } from '@/hooks/use-toast'
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth'

export function SettingsPage() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()
  const { toast } = useToast()

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm({
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  })

  const onPasswordSubmit = async (data: any) => {
    if (data.new_password !== data.confirm_password) {
      toast({
        title: 'Password mismatch',
        description: 'New passwords do not match.',
        variant: 'destructive',
      })
      return
    }

    if (!user?.email) {
      toast({
        title: 'Error',
        description: 'User email not found',
        variant: 'destructive',
      })
      return
    }

    setIsChangingPassword(true)
    try {
      // Re-authenticate user before changing password (Firebase requirement)
      const credential = EmailAuthProvider.credential(
        user.email,
        data.current_password
      )
      await reauthenticateWithCredential(user, credential)

      // Update password
      await updatePassword(user, data.new_password)

      resetPassword()
      toast({
        title: 'Password changed',
        description: 'Your password has been successfully updated.',
      })
    } catch (error: any) {
      let errorMessage = 'Failed to change password'
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect'
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak'
      }

      toast({
        title: 'Password change failed',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleNotificationChange = async (_field: string, _value: boolean) => {
    // TODO: Implement notification preferences with Firebase/backend
    toast({
      title: 'Coming soon',
      description: 'Notification preferences will be available soon.',
    })
  }

  const handleExportData = () => {
    toast({
      title: 'Export requested',
      description: 'Your data export will be emailed to you within 24 hours.',
    })
  }

  const handleDeleteAccount = () => {
    toast({
      title: 'Account deletion',
      description: 'Please contact support to delete your account.',
      variant: 'destructive',
    })
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Investor Profile - Only if user is investor (check via profile) */}
      {/* We can optimistically show it or check user role if available in user object, 
          but simpler to just fetch profile and show if exists */}

      <InvestorSettingsSection />

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Appearance
          </CardTitle>
          <CardDescription>
            Customize the look and feel of the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Theme</Label>
              <p className="text-sm text-muted-foreground">
                Choose your preferred theme
              </p>
            </div>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </CardTitle>
          <CardDescription>
            Configure how you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <input
              type="checkbox"
              defaultChecked={true}
              onChange={(e) => handleNotificationChange('email_notifications', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
          </div>

          <Separator />

          <div className="space-y-3">
            <Label className="text-base">Email Preferences</Label>
            <div className="space-y-2 ml-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-normal">Analysis Completed</Label>
                  <p className="text-xs text-muted-foreground">
                    When your startup analysis is completed
                  </p>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-normal">New Comments</Label>
                  <p className="text-xs text-muted-foreground">
                    When someone comments on your startup
                  </p>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-normal">Weekly Summary</Label>
                  <p className="text-xs text-muted-foreground">
                    Weekly digest of platform activity
                  </p>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security
          </CardTitle>
          <CardDescription>
            Manage your account security settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-base">Change Password</Label>
            <p className="text-sm text-muted-foreground mb-4">
              Update your password to keep your account secure
            </p>

            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    {...registerPassword('current_password', { required: 'Current password is required' })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {passwordErrors.current_password && (
                  <p className="text-sm text-destructive">{passwordErrors.current_password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    {...registerPassword('new_password', {
                      required: 'New password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' }
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {passwordErrors.new_password && (
                  <p className="text-sm text-destructive">{passwordErrors.new_password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  {...registerPassword('confirm_password', { required: 'Please confirm your password' })}
                />
                {passwordErrors.confirm_password && (
                  <p className="text-sm text-destructive">{passwordErrors.confirm_password.message}</p>
                )}
              </div>

              <Button type="submit" disabled={isChangingPassword}>
                <Save className="w-4 h-4 mr-2" />
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </div>

          <Separator />

          <div>
            <Label className="text-base">Two-Factor Authentication</Label>
            <p className="text-sm text-muted-foreground mb-4">
              Add an extra layer of security to your account
            </p>
            <div className="flex items-center gap-2">
              <Badge variant="outline">Not Enabled</Badge>
              <Button variant="outline" size="sm" disabled>
                Enable 2FA (Coming Soon)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy */}
      <Card>
        <CardHeader>
          <CardTitle>Data & Privacy</CardTitle>
          <CardDescription>
            Manage your data and privacy settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Export Data</Label>
              <p className="text-sm text-muted-foreground">
                Download a copy of all your data
              </p>
            </div>
            <Button variant="outline" onClick={handleExportData}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Data Retention</Label>
              <p className="text-sm text-muted-foreground">
                How long we keep your data
              </p>
            </div>
            <Badge variant="outline">7 years</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Delete Account</Label>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all associated data
              </p>
            </div>
            <Button variant="destructive" onClick={handleDeleteAccount}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function InvestorSettingsSection() {
  const navigate = useNavigate();
  // Explicitly typing as any to avoid 'unknown' thesis error without full type definition
  const { data: investorProfile, isLoading } = useQuery({
    queryKey: ['investor-profile'],
    queryFn: investorApi.getProfile,
    retry: false,
  });

  // If 404 or not investor, we might want to hide this. 
  // Assuming backend returns error or null if not investor.
  if (isLoading || !investorProfile) return null;

  const thesisCount = Array.isArray((investorProfile as any).thesis) ? (investorProfile as any).thesis.length : 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Investor Profile
          </CardTitle>
          <CardDescription>
            Manage your investment criteria and thesis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Investment Thesis</Label>
              <p className="text-sm text-muted-foreground">
                {thesisCount > 0
                  ? `${thesisCount} pillars defined`
                  : 'Define your investment strategy to get better recommendations'}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/thesis-builder')}>
              {thesisCount > 0 ? 'Update Thesis' : 'Create Thesis'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
